import React, { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { Users } from "lucide-react";
import VerifyCustStatusButton from "../components/Customer/Ppp/VerifyCustStatusButton.js";
import EnableDisablePppModal from "../components/Customer/Ppp/EnableDisablePppModal.js";
import PppSecretDetailModal from "../components/Customer/Ppp/PppSecretDetailModal.js";

const AllPppSecret = () => {
  const token = localStorage.getItem("token");

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(500);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);

  const [selectedRow, setSelectedRow] = useState(null);

  const [activeModal, setActiveModal] = useState(null);
  // null | "detail" | "confirm"

  const [filters, setFilters] = useState({
    name: "",
    disabled: "",
    bill_status: "",
    cust_status: "",
    customer_id: "",
    device_id: "",
  });

  const [quickSearch, setQuickSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/ppp-secret", {
        headers: { Authorization: token },
        params: {
          page,
          limit,
          name: filters.name || undefined,
          disabled: filters.disabled || undefined,
          bill_status: filters.bill_status || undefined,
          cust_status: filters.cust_status || undefined,
          customer_id: filters.customer_id || undefined,
          device_id: filters.device_id || undefined,
        },
      });

      setData(res.data.data);
      setFilteredData(res.data.data);
      setTotalBackend(res.data.pagination.total);
      setTotalPages(res.data.pagination.totalPages);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch PPP Secret data");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** QUICK SEARCH (frontend) */
  useEffect(() => {
    const keyword = quickSearch.toLowerCase();

    const filtered = data.filter((row) =>
      [row.name, row.profile, row.comment]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );

    setFilteredData(filtered);
  }, [quickSearch, data]);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const openDetailModal = (row) => {
    setSelectedRow(row);
    setActiveModal("detail");
  };

  const openConfirmModal = (row) => {
    setSelectedRow(row);
    setActiveModal("confirm"); // ✅ FIX
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedRow(null);
  };

  return (
    <div className="p-6 dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-200">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6" />
          All PPP Secrets
        </h1>
        <VerifyCustStatusButton token={token} onSuccess={fetchData} />
      </div>

      {/* FILTER SECTION */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
        <input
          type="text"
          name="name"
          placeholder="Search name"
          value={filters.name}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <select
          name="disabled"
          value={filters.disabled}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">Any</option>
          <option value="0">enabled</option>
          <option value="1">disabled</option>
        </select>

        <select
          name="bill_status"
          value={filters.bill_status}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">All Bill Status</option>
          <option value="paid">paid</option>
          <option value="unpaid">unpaid</option>
          <option value="not found">not found</option>
        </select>

        <select
          name="cust_status"
          value={filters.cust_status}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">All Customer Status</option>
          <option value="active">active</option>
          <option value="not found">not found</option>
          <option value="closed">closed</option>
          <option value="Unknown">unknown</option>
        </select>

        <input
          type="text"
          name="customer_id"
          placeholder="Customer ID"
          value={filters.customer_id}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <input
          type="text"
          name="device_id"
          placeholder="Device / BTS ID"
          value={filters.device_id}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <button
          onClick={handleSearch}
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Search
        </button>
      </div>

      {/* QUICK SEARCH */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Quick search (name / profile / comment)"
          value={quickSearch}
          onChange={(e) => {
            setPage(1);
            setQuickSearch(e.target.value);
          }}
          className="w-full max-w-sm px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <div className="flex gap-4 text-sm">
          <span>
            Total Data: <b>{totalBackend}</b>
          </span>
          <span>
            Showing: <b>{filteredData.length}</b>
          </span>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded border dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left">Device</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Profile</th>
              <th className="px-4 py-2 text-center">Comment</th>
              <th className="px-4 py-2 text-center">Note</th>
              <th className="px-4 py-2 text-center">Customer Bill</th>
              <th className="px-4 py-2 text-center">Customer Status</th>
              <th className="px-4 py-2 text-center">Disabled</th>
              <th className="px-4 py-2 text-center">Updated at</th>
              <th className="px-4 py-2 text-center"></th>
              <th className="px-4 py-2 text-center"></th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center">
                  Tidak ada data
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr
                  key={row.id}
                  className="border-t dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <td className="px-4 py-2">
                    {row.device_id} | {row.device_name}
                  </td>
                  <td className="px-4 py-2">{row.name}</td>
                  <td className="px-4 py-2">{row.profile || "-"}</td>
                  <td className="px-4 py-2 text-center">
                    {row.comment || "-"}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {row.admin_note || "-"}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        row.bill_status === "paid"
                          ? "bg-green-100 text-green-700"
                          : row.bill_status === "unpaid"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {row.bill_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        row.cust_status === "active"
                          ? "bg-green-100 text-green-700"
                          : row.cust_status === "Unknown"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {row.cust_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {row.disabled ? (
                      <span className="text-red-500">Yes</span>
                    ) : (
                      <span className="text-green-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {new Date(row.updated_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-center space-x-2">
                    <button
                      onClick={() => openDetailModal(row)}
                      className="bg-blue-600 text-white px-2 py-1 text-xs rounded"
                    >
                      Detail
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center space-x-2">
                    <button
                      onClick={() => openConfirmModal(row)}
                      className={`px-2 py-1 text-xs rounded text-white ${
                        row.disabled
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      {row.disabled ? "Enable" : "Disable"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <EnableDisablePppModal
          isOpen={activeModal === "confirm"}
          row={selectedRow}
          onClose={closeModal}
          onSuccess={fetchData}
        />

        <PppSecretDetailModal
          isOpen={activeModal === "detail"}
          onClose={closeModal}
          data={selectedRow}
        />
      </div>

      {/* PAGINATION */}
      <div className="mt-4 flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Prev
        </button>

        <span>
          Page {page} / {totalPages}
        </span>

        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AllPppSecret;
